// People (DESIGN §3 + §8): /people — the team (alphabetical): name + department, login ID, role, active. Everyone sees
// the list; admins edit inline (Role, Department, Active, Admin) and use the row menu (Edit details, Reset password,
// Remove) and Add member. /people/:id?range= — one person: compact stats, their open batches, their score trend.
// Never ranked or compared side by side.
import './people.css';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { MODE, canManageTeam, dispatch, resetPassword, useStore } from '../store.js';
import { href, personHref, setQuery } from '../router.js';
import { Icon } from '../icons.jsx';
import { Page } from '../components/page.jsx';
import { Panel } from '../components/panel.jsx';
import { BatchTable } from '../components/batch-row.jsx';
import { ChoiceSelect, choiceOptions } from '../components/choice-select.jsx';
import { openTeamMember } from '../components/forms.jsx';
import { MenuList, Popover, usePopover } from '../components/popover.jsx';
import { Avatar, Button, EmptyState, Field, TextInput, cx, toast } from '../components/ui.jsx';
import { KpiCards, RangeButtons, ScoreCard, rangeOf } from './performance.jsx';
import { removalText } from './people-removal.js';
import { personStats } from '../../shared/derive.js';
import { DEFAULT_RANGE, DEPARTMENTS, PASSWORD_MAX, PASSWORD_MIN, TEAM_ROLES, plural } from '../../shared/constants.js';
import { formatDateRange } from '../../shared/dates.js';
import { normalizeRange } from '../../shared/performance.js';

// Open batches shown before "Show more" (keeps the score chart within reach).
const OPEN_LIMIT = 8;
const byName = (a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
const isActive = (m) => m.active !== false;
const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || name;

// After a member is removed their row (and the menu that removed them) is gone: keep focus on the page (A11Y-20).
const focusAddButton = () => setTimeout(() => document.querySelector('.people-add, .page-title')?.focus(), 0);

// ---------- /people ----------

export function PeopleView() {
  const snap = useStore();
  const { state, currentPerson } = snap;
  const admin = canManageTeam(snap);
  const accounts = MODE === 'server' && snap.authRequired !== false; // passwords exist only on a server with sign-in
  const team = useMemo(() => [...state.team].sort(byName), [state.team]);
  const roles = useMemo(() => choiceOptions(TEAM_ROLES, state.team.map((t) => t.role)), [state.team]);
  const departments = useMemo(() => choiceOptions(DEPARTMENTS, state.team.map((t) => t.department)), [state.team]);

  const active = team.filter(isActive).length;
  const subtitle = !team.length ? '' : active < team.length ? `${plural(team.length, 'member')} · ${active} active` : plural(team.length, 'member');
  const add = admin ? <Button variant="primary" icon="plus" class="people-add" onClick={() => openTeamMember()}>Add member</Button> : undefined;
  return (
    <Page title="People" subtitle={subtitle} actions={add}>
      {team.length ? (
        <Panel flush label="Team members">
          <div class={cx('people-table', admin && 'is-admin', admin && !accounts && 'no-accounts')} role="table" aria-label="Team members">
            <div class="people-row people-head" role="row">
              <span role="columnheader">Member</span>
              <span role="columnheader">Login ID</span>
              <span role="columnheader">Role</span>
              {admin && <span role="columnheader" class="people-center">Active</span>}
              {admin && accounts && <span role="columnheader" class="people-center">Admin</span>}
              {admin && <span role="columnheader"><span class="sr-only">More</span></span>}
            </div>
            {team.map((m) => (
              <PersonRow key={m.id} member={m} self={m.id === currentPerson} admin={admin} accounts={accounts}
                roles={roles} departments={departments} />
            ))}
          </div>
        </Panel>
      ) : (
        <Panel>
          <EmptyState title="No team members yet." description="Add the people who make the videos, then assign batches to them." />
        </Panel>
      )}
    </Page>
  );
}

// One member. Admin edits save at once (optimistic: `pending` shows the new value until the store confirms or the
// server refuses). Nobody can switch off their own Active or Admin — no locking yourself out.
function PersonRow({ member, self, admin, accounts, roles, departments }) {
  const [pending, setPending] = useState({});
  const shown = { ...member, ...pending };
  const active = isActive(shown);
  const name = firstName(member.name);
  const save = (patch, message) => {
    setPending((p) => ({ ...p, ...patch }));
    dispatch('team.update', { id: member.id, patch })
      .then(() => { if (message) toast(message, { tone: 'success' }); })
      .catch(() => {})
      .finally(() => setPending((p) => {
        const next = { ...p };
        for (const k of Object.keys(patch)) delete next[k];
        return next;
      }));
  };
  const selfNote = self ? 'You can’t change this for yourself.' : undefined;

  return (
    <div class={cx('people-row', !active && 'is-inactive')} role="row">
      <div class="people-member" role="rowheader">
        <Avatar name={member.name} size={32} />
        <div class="people-who">
          <a class="people-name" href={personHref(member.id)}>
            <span class="truncate">{member.name}</span>
            {self && <span class="people-you">You</span>}
            {!admin && accounts && member.isAdmin && <span class="people-tag" title="Admins reset passwords and manage people">Admin</span>}
            {!admin && !active && <span class="people-tag is-off">Inactive</span>}
          </a>
          {admin ? (
            <ChoiceSelect class="people-dept" size="sm" value={shown.department || ''} options={departments} label={`Department: ${member.name}`}
              placeholder="No department" customPlaceholder="Department" onValue={(v) => save({ department: v })} />
          ) : (
            // Phones: "Role · Department" on one line (the Role column is hidden there).
            (member.department || member.role) && (
              <span class="people-dept-text truncate">
                {member.role && <span class="show-sm">{member.role}{member.department ? ' · ' : ''}</span>}
                {member.department}
              </span>
            )
          )}
        </div>
      </div>
      <div class="people-login" role="cell"><span class="login-id truncate">{member.loginId || '—'}</span></div>
      <div class="people-role" role="cell">
        {admin ? (
          <ChoiceSelect size="sm" value={shown.role || ''} options={roles} label={`Role: ${member.name}`} placeholder="No role" customPlaceholder="Role"
            onValue={(v) => save({ role: v })} />
        ) : (
          <span class="truncate">{member.role || '—'}</span>
        )}
      </div>
      {admin && (
        <label class="people-center" role="cell">
          <input type="checkbox" class="people-check" checked={active} disabled={self} title={selfNote || (active ? 'Can sign in' : 'Can’t sign in')}
            aria-label={`Active: ${member.name}`}
            onChange={(e) => save({ active: e.currentTarget.checked }, e.currentTarget.checked ? `${name} can sign in again.` : `${name} can no longer sign in.`)} />
        </label>
      )}
      {admin && accounts && (
        <label class="people-center" role="cell">
          <input type="checkbox" role="switch" class="switch" checked={shown.isAdmin === true} disabled={self} title={selfNote || 'Admins manage people'}
            aria-label={`Admin: ${member.name}`}
            onChange={(e) => save({ isAdmin: e.currentTarget.checked }, e.currentTarget.checked ? `${name} is now an admin.` : `${name} is no longer an admin.`)} />
        </label>
      )}
      {admin && (
        <div class="people-more" role="cell">
          <RowMenu member={member} self={self} accounts={accounts} />
        </div>
      )}
    </div>
  );
}

// Row menu (admins): Edit details · Reset password (server sign-in only) · Remove. Reset and Remove swap the menu for
// a small inline step inside the same popover.
function RowMenu({ member, self, accounts }) {
  const pop = usePopover();
  const [step, setStep] = useState('menu');
  const close = () => { pop.close(); setStep('menu'); };
  const items = [
    { label: 'Edit details', icon: 'edit', onSelect: () => openTeamMember(member, { onRemoved: focusAddButton }) },
    accounts && !self && { label: 'Reset password', icon: 'key', onSelect: () => setStep('reset'), keepOpen: true }, // own: Settings
    { divider: true },
    { label: 'Remove', icon: 'trash', danger: true, disabled: self, onSelect: () => setStep('remove'), keepOpen: true },
  ].filter(Boolean);
  const menu = step === 'menu';
  return (
    <>
      <button ref={pop.anchorRef} type="button" class="icon-btn icon-btn-sm" aria-label={`More for ${member.name}`} title="More" {...pop.triggerProps}>
        <Icon name="more" size={16} />
      </button>
      <Popover open={pop.open} onClose={close} anchorRef={pop.anchorRef} width={menu ? 200 : 280} role={menu ? 'menu' : 'dialog'}
        label={menu ? `${member.name}` : step === 'reset' ? `Reset password for ${member.name}` : `Remove ${member.name}`}
        class={menu ? 'popover-menu' : 'people-pop'}>
        {menu && <MenuList items={items} onClose={close} />}
        {step === 'reset' && <ResetPassword member={member} onDone={close} onBack={() => setStep('menu')} />}
        {step === 'remove' && <RemoveMember member={member} accounts={accounts} onBack={() => setStep('menu')} />}
      </Popover>
    </>
  );
}

function ResetPassword({ member, onDone, onBack }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    if (value.length < PASSWORD_MIN) {
      setError(`Use at least ${PASSWORD_MIN} characters.`);
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    resetPassword(member.id, value)
      .then(() => { toast(`${firstName(member.name)}’s password was reset.`, { tone: 'success' }); onDone(); })
      .catch((err) => { setError(err.message || 'Could not reset the password.'); setBusy(false); });
  };
  return (
    <form class="people-step" onSubmit={submit} noValidate>
      <p class="people-step-title">Reset password</p>
      <p class="people-step-text">{firstName(member.name)} signs in with this until they change it in Settings.</p>
      {/* Shown as typed: the admin passes it on, so a typo must be visible (the member changes it after signing in). */}
      <Field label="New password" hint={`At least ${PASSWORD_MIN} characters.`} error={error}>
        <TextInput inputRef={inputRef} type="text" name="new-password" autoComplete="off" spellCheck={false} autoCapitalize="off" maxLength={PASSWORD_MAX}
          value={value} invalid={!!error} onValue={(v) => { setValue(v); if (error) setError(''); }} />
      </Field>
      <div class="people-step-actions">
        <Button size="sm" variant="ghost" onClick={onBack}>Cancel</Button>
        <Button size="sm" variant="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Set password'}</Button>
      </div>
    </form>
  );
}

function RemoveMember({ member, accounts, onBack }) {
  const { state } = useStore();
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef(null);
  useEffect(() => { confirmRef.current?.focus(); }, []);
  const remove = () => {
    setBusy(true);
    dispatch('team.delete', { id: member.id })
      .then(() => { toast(`${member.name} removed.`); focusAddButton(); })
      .catch(() => setBusy(false));
  };
  return (
    <div class="people-step">
      <p class="people-step-title">Remove {member.name}?</p>
      <p class="people-step-text">
        {removalText(state, member, accounts)}
      </p>
      <div class="people-step-actions">
        <Button size="sm" variant="ghost" onClick={onBack}>Cancel</Button>
        <button ref={confirmRef} type="button" class="btn btn-sm btn-danger" disabled={busy} onClick={remove}>{busy ? 'Removing…' : 'Remove'}</button>
      </div>
    </div>
  );
}

// ---------- /people/:id ----------

export function PersonView({ route }) {
  const snap = useStore();
  const { state, index, today } = snap;
  const admin = canManageTeam(snap);
  const id = route.params.memberId;
  const range = normalizeRange(route.query.range || DEFAULT_RANGE);
  const stats = useMemo(() => personStats(state, index, id, { range, today }), [state, index, id, range, today]);
  const [showAll, setShowAll] = useState(false);

  if (!stats) {
    return (
      <Page title="Person not found">
        <Panel>
          <EmptyState title="This person is not on the team." description="They may have been removed."
            action={<Button href={href('/people')} icon="chevronLeft">Back to People</Button>} />
        </Panel>
      </Page>
    );
  }

  // "Show more" disappears once clicked: move focus to the first newly shown row instead of <body>.
  const showMore = () => {
    setShowAll(true);
    setTimeout(() => document.querySelectorAll('.person-batches a.batch-row')[OPEN_LIMIT]?.focus(), 0);
  };
  const blocked = stats.openBatches.filter((r) => r.status === 'Blocked').length;
  const actions = (
    <div class="person-actions">
      <RangeButtons value={range} onChange={(v) => setQuery({ range: v === DEFAULT_RANGE ? null : v })} />
      {admin && <Button icon="edit" onClick={() => openTeamMember(stats.person)}>Edit</Button>}
    </div>
  );
  return (
    <Page title={stats.name} meta={stats.role} subtitle={formatDateRange(stats.period.start, stats.period.end)} actions={actions}>
      <div class="perf person">
        <KpiCards kpis={stats.kpis} summary={{ blocked }} outputLabel="Completed" />
        <Panel title="Open batches" meta={stats.openBatches.length || ''} flush class="person-batches">
          <BatchTable
            batches={stats.openBatches.map((r) => r.batch)}
            index={index}
            today={today}
            showOwner={false}
            label={`${stats.name}'s open batches`}
            limit={showAll ? 0 : OPEN_LIMIT}
            onMore={showMore}
            empty={<EmptyState compact title="No open batches." description={`Batches assigned to ${id === snap.currentPerson ? 'you' : 'them'} show up here.`} />}
          />
        </Panel>
        <ScoreCard title="Score" perf={stats} range={range}
          emptyText={`Nothing finished or due for ${stats.name.split(' ')[0]} in the ${rangeOf(range).long.toLowerCase()}.`} />
      </div>
    </Page>
  );
}
